import { DropdownField, Flex, Modal, Text, TextField, useMediaQuery, useToast } from "@wayflyer/flyui";
import type { BankAccountGroupSchema } from "bankconnect";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";

import { useMoveStatementToGroup, useUnassignStatement } from "../../../../queries/bank-connect/queries";
import { MESSAGES } from "../message-definitions";
import { formatInstitutionLabel, getGroupAccountLabel } from "../utils";

const CREATE_NEW_GROUP_ID = "__create_new_account__";
// Mirrors BankAccountGroup.institution_name/account_name's 120-char column limit, so an overlong
// value can't slip past this form and 422 on submit.
const NAME_MAX_LENGTH = 120;

// Mirrors SUPPORTED_CURRENCIES in core/analysis/currency/conversion.py - the currencies Wayflyer
// can actually price and convert. Deliberately not shared-utils' currencyOptions (only 7
// currencies) or capital/schemas/tranche_enums.py::Currency (a separate, narrower 17-value enum
// out of sync with pricing) - see ONB-474 PR description for the flag to eng about consolidating
// these three lists.
const TOP_CURRENCIES = ["USD", "AUD", "GBP", "EUR", "CAD"];
const OTHER_SUPPORTED_CURRENCIES = [
  "NZD",
  "SEK",
  "CHF",
  "PLN",
  "DKK",
  "TRY",
  "SGD",
  "HKD",
  "JPY",
  "CNY",
  "HUF",
  "NOK",
  "RON",
  "MXN",
  "ZAR",
  "CZK",
  "INR",
  "THB",
  "PHP",
  "IDR",
  "ILS",
  "KRW",
  "MYR",
  "BRL",
  "ISK",
];
const currencyOptions = [
  { id: "top-currencies", name: null, items: TOP_CURRENCIES.map((code) => ({ id: code, text: code })) },
  { id: "other-currencies", name: null, items: OTHER_SUPPORTED_CURRENCIES.map((code) => ({ id: code, text: code })) },
];

type AssignStatementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  uploadUuid: string;
  // Disambiguates which membership to move for a multi-account upload; omit if ungrouped.
  memberUuid?: string;
  currentGroupUuid: string | null;
  accountGroups: BankAccountGroupSchema[];
  companyDomain: string;
  onMoved: () => void;
};

export const AssignStatementModal = ({
  isOpen,
  onClose,
  fileName,
  uploadUuid,
  memberUuid,
  currentGroupUuid,
  accountGroups,
  companyDomain,
  onMoved,
}: AssignStatementModalProps) => {
  const intl = useIntl();
  const posthog = usePostHog();
  const toast = useToast();
  const query = useMediaQuery();
  const isMobile = query("sm").windowLte;
  const { openErrorModal } = useErrorModal();
  const { mutate, mutateAsync: moveAsync, isPending } = useMoveStatementToGroup(companyDomain);
  const { mutateAsync: unassignAsync } = useUnassignStatement(companyDomain);
  const [selectedId, setSelectedId] = useState<string | null>(currentGroupUuid);
  const [newBankName, setNewBankName] = useState("");
  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountType, setNewAccountType] = useState<string | null>(null);
  const [newCurrency, setNewCurrency] = useState<string | null>(null);
  const [newMask, setNewMask] = useState("");

  const isCreatingNew = selectedId === CREATE_NEW_GROUP_ID;

  // "Keep here" (the statement's current group) always leads the list, regardless of where that
  // group falls in accountGroups.
  const currentGroup = accountGroups.find((group) => group.uuid === currentGroupUuid);
  const otherGroups = accountGroups.filter((group) => group.uuid !== currentGroupUuid);
  // Flat list, not grouped sections - the Figma reference (node 794:137610) shows "Add a new
  // account" as a plain trailing item with the same spacing as the others, not set off by a
  // divider (grouped DropdownField options render one automatically between sections).
  const options = [
    ...(currentGroup ? [{ id: currentGroup.uuid, text: intl.formatMessage(MESSAGES.keepHereOption) }] : []),
    ...otherGroups.map((group) => ({ id: group.uuid, text: getGroupAccountLabel(intl, group) })),
    { id: CREATE_NEW_GROUP_ID, text: intl.formatMessage(MESSAGES.addNewAccountOption) },
  ];

  const accountTypeOptions = [
    { id: "CHECKING", text: intl.formatMessage(MESSAGES.accountTypeChecking) },
    { id: "SAVINGS", text: intl.formatMessage(MESSAGES.accountTypeSavings) },
    { id: "CREDITCARD", text: intl.formatMessage(MESSAGES.accountTypeCreditCard) },
    { id: "UNKNOWN", text: intl.formatMessage(MESSAGES.accountTypeOther) },
  ];

  const hasSelection = selectedId !== null;
  const hasChanged = selectedId !== currentGroupUuid;
  const canCreateNew =
    newBankName.trim() !== "" && newAccountName.trim() !== "" && newAccountType !== null && newCurrency !== null;
  const canApply = isCreatingNew ? canCreateNew : hasSelection && hasChanged;

  // Undo restores the pre-move group: another move if it still has other members, otherwise a
  // recreate from the accountGroups snapshot, since moving the last member away deleted it (see
  // move_statement_to_group). Omitted if that snapshot can't satisfy create_new's requirements.
  //
  // Chains the *Async variants' promise rather than mutate()'s onSuccess/onError - this modal
  // unmounts right after showing the toast, and react-query drops those callbacks once nothing
  // is still subscribed to the mutation.
  const buildUndoAction = (): { content: string; onAction: () => void } | undefined => {
    const onUndone = () => {
      posthog?.capture("onboarding_bank_file_reassignment_cancelled");
      onMoved();
    };
    const onError = (error: Error) => openErrorModal({ variant: "submit-error", error });
    const content = intl.formatMessage(MESSAGES.assignStatementUndoAction);

    if (currentGroupUuid === null) {
      return {
        content,
        onAction: () => {
          unassignAsync({ uploadUuid, params: { memberUuid } }).then(onUndone).catch(onError);
        },
      };
    }

    if (!currentGroup) {
      return undefined;
    }

    const wasOnlyMember = currentGroup.statementMembers.length + currentGroup.connectionMembers.length <= 1;
    if (!wasOnlyMember) {
      return {
        content,
        onAction: () => {
          moveAsync({ uploadUuid, params: { targetGroupUuid: currentGroupUuid, memberUuid } })
            .then(onUndone)
            .catch(onError);
        },
      };
    }

    const { institutionName, accountName, accountType, currency, mask } = currentGroup;
    if (!institutionName || !accountType || !currency) {
      return undefined;
    }
    return {
      content,
      onAction: () => {
        moveAsync({
          uploadUuid,
          params: {
            createNew: true,
            newAccountInstitutionName: institutionName,
            newAccountName: accountName || institutionName,
            newAccountType: accountType,
            newAccountCurrency: currency,
            newAccountMask: mask ?? undefined,
            memberUuid,
          },
        })
          .then(onUndone)
          .catch(onError);
      },
    };
  };

  const handleApply = () => {
    if (!canApply) {
      return;
    }
    mutate(
      {
        uploadUuid,
        params: isCreatingNew
          ? {
              createNew: true,
              newAccountInstitutionName: newBankName.trim(),
              newAccountName: newAccountName.trim(),
              newAccountType,
              newAccountCurrency: newCurrency,
              newAccountMask: newMask.trim() || undefined,
              memberUuid,
            }
          : { targetGroupUuid: selectedId, memberUuid },
      },
      {
        onSuccess: () => {
          posthog?.capture("onboarding_bank_file_reassigned", {
            destination: isCreatingNew ? "create_account" : "existing_account",
            was_unassigned: currentGroupUuid === null,
          });
          const targetGroup = accountGroups.find((group) => group.uuid === selectedId);
          const accountName = isCreatingNew
            ? formatInstitutionLabel(intl, newBankName.trim(), newMask.trim())
            : targetGroup && getGroupAccountLabel(intl, targetGroup);
          if (accountName) {
            toast.info(intl.formatMessage(MESSAGES.assignStatementSuccessToast, { fileName, accountName }), {
              duration: 5000,
              action: buildUndoAction(),
            });
          }
          onMoved();
          onClose();
        },
        onError: (error) => {
          openErrorModal({ variant: "submit-error", error });
        },
      }
    );
  };

  return (
    <Modal
      testId="assign-statement-modal"
      isOpen={isOpen}
      onClose={onClose}
      title={intl.formatMessage(MESSAGES.assignStatementTitle, { fileName })}
      size="normal"
      primaryAction={{
        content: intl.formatMessage(
          isCreatingNew ? MESSAGES.addNewAccountConfirmAction : MESSAGES.assignStatementApplyAction
        ),
        onAction: handleApply,
        disabled: !canApply,
        loading: isPending,
      }}
    >
      <Flex direction="column" gap="4">
        <Text size="sm">{intl.formatMessage(MESSAGES.assignStatementBodyText)}</Text>
        <DropdownField
          name="assign-statement-target"
          options={options}
          controlledValue={selectedId}
          onChange={(id) => setSelectedId(id)}
          placeholder={intl.formatMessage(MESSAGES.assignStatementPlaceholder)}
        />

        {isCreatingNew && (
          <>
            <TextField
              name="new-account-bank-name"
              label={intl.formatMessage(MESSAGES.addNewAccountBankNameLabel)}
              value={newBankName}
              onChange={(event) => setNewBankName(event.target.value.slice(0, NAME_MAX_LENGTH))}
            />
            <TextField
              name="new-account-name"
              label={intl.formatMessage(MESSAGES.addNewAccountNameLabel)}
              value={newAccountName}
              onChange={(event) => setNewAccountName(event.target.value.slice(0, NAME_MAX_LENGTH))}
            />
            <DropdownField
              name="new-account-type"
              label={intl.formatMessage(MESSAGES.addNewAccountTypeLabel)}
              options={accountTypeOptions}
              controlledValue={newAccountType}
              onChange={(id) => setNewAccountType(id)}
            />
            <Flex direction={isMobile ? "column" : "row"} gap="4" align={isMobile ? undefined : "flex-end"}>
              <Flex width={isMobile ? undefined : "102px"} minWidth={isMobile ? undefined : "102px"} direction="column">
                <DropdownField
                  name="new-account-currency"
                  label={intl.formatMessage(MESSAGES.addNewAccountCurrencyLabel)}
                  options={currencyOptions}
                  controlledValue={newCurrency}
                  onChange={(id) => setNewCurrency(id)}
                />
              </Flex>
              <Flex grow={isMobile ? undefined : 1} minWidth={isMobile ? undefined : "0"} direction="column">
                <TextField
                  name="new-account-mask"
                  label={intl.formatMessage(MESSAGES.addNewAccountMaskLabel)}
                  value={newMask}
                  onChange={(event) => setNewMask(event.target.value.replace(/\D/g, "").slice(0, 4))}
                  optional
                />
              </Flex>
            </Flex>
          </>
        )}
      </Flex>
    </Modal>
  );
};
